import React from 'react';
import { Helmet } from 'react-helmet-async';
import { withTranslation, Trans } from 'react-i18next';
import qs from 'qs';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Form from 'components/form';
import TextField from 'components/form/TextInput';
import Button from 'components/Button';
import toast from 'utils/toast';
import { resetPassword, initVerifyNumber } from 'services/users';
import cookie from 'utils/cookie';
import * as Styled from '../login/styled';
import Paper from '@mui/material/Paper';
import OTPInput from 'components/form/OTPInput';

const FormItem = Form.FormItem;

class ResetPassword extends React.Component {
  state = {
    token: '',
    needVerification: false,
    last4: '',
    enableSend: false,
    loading: true,
    invalid: false,
    timeLeft: 30,
    ready: false,
  };

  componentDidMount() {
    const { t } = this.props;
    const query = qs.parse(this.props.location.search.substr(1));
    if (!query.token) {
      toast.error(t('auth:missing-token-error', 'Invalid Request'));
      this.props.history.push('/login');
    } else {
      const deviceId = cookie.get('device_id');
      this.setState(query);
      initVerifyNumber(query.token, null, deviceId)
        .then((resp) => {
          this.setState({
            device_id: resp.device_id,
            needVerification: resp.needs_verification === '1',
            last4: resp.account_mobile,
            loading: false,
          });
          this.initTimer();
          cookie.set('device_id', resp.device_id, { path: '/' });
        })
        .catch((e) => {
          this.setState({
            loading: false,
            invalid: true,
          });
          toast.error(e.message);
        })
        .finally(() => {
          this.setState({
            ready: true,
          });
        });
    }
    try {
      cookie.remove('token', { path: '/' });
      cookie.remove('username', { path: '/' });
    } catch (e) {}
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  initTimer = () => {
    this.setState({
      timeLeft: 29,
    });
    this.interval = setInterval(() => {
      const { timeLeft } = this.state;
      if (timeLeft === 0) {
        this.setState({
          enableSend: true,
        });
        clearInterval(this.interval);
      } else {
        this.setState({
          timeLeft: timeLeft - 1,
        });
      }
    }, 1000);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { t, form } = this.props;
    return form.validateFields().then((values) => {
      if (values.password !== values.confirmPassword) {
        toast.error(t('auth:password-do-not-match', 'Password do not match'));
      } else {
        return resetPassword(values.password, this.state.token).then(
          () => {
            toast.success(
              t(
                'auth:password-saved-success',
                'Your password is saved. You can now login',
              ),
            );
            this.props.history.push('/login');
          },
          (err) => toast.error(err.message),
        );
      }
    });
  };

  onOTPCompleted = (val) => {
    const { t } = this.props;
    clearInterval(this.interval);
    this.setState({ loading: true });
    return initVerifyNumber(this.state.token, val, this.state.device_id)
      .then(
        (resp) => {
          this.setState({
            needVerification: resp.needs_verification === '1',
          });
        },
        (e) =>
          toast.error(
            e.message ||
              t(
                'auth:invalid-verification-code-error',
                'Invalid verification code',
              ),
          ),
      )
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  resendCode = () => {
    const { t } = this.props;
    return initVerifyNumber(this.state.token)
      .then((resp) => {
        this.setState({
          needVerification: resp.needs_verification === '1',
          last4: resp.account_mobile,
          enableSend: false,
          loading: false,
        });
        toast.success(
          t('auth:verification-code-sent', {
            defaultValue: `Verification code sent to phone number ending with {{number}}`,
            number: resp.account_mobile,
          }),
        );
        this.initTimer();
      })
      .catch((e) => {
        this.setState({
          loading: false,
        });
        toast.error(e.message);
      });
  };

  render() {
    const {
      form: { getFieldDecorator },
      t,
    } = this.props;
    const {
      last4,
      loading,
      needVerification,
      enableSend,
      timeLeft,
      invalid,
      ready,
    } = this.state;
    return (
      <Styled.Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 8,
        }}
      >
        <Helmet title={t('create-password', 'Create Password')} />
        <Box maxWidth={500} mx="auto">
          <Form>
            <>
              <Box textAlign="center" mb={3} className="logo">
                <img src={`/assets/svg/tic-logo-full.svg`} alt="logo" />
              </Box>
              {ready && (
                <Paper className="form-container" sx={{ p: 3 }}>
                  {!needVerification ? (
                    <Typography
                      paragraph
                      variant="h3"
                      fontWeight={400}
                      lineHeight={1.2}
                    >
                      <Trans ns="auth" i18nKey="create-password-hint">
                        Please enter your password below. You will use this
                        password along with your email to login.
                      </Trans>
                    </Typography>
                  ) : (
                    <Typography
                      variant="h1"
                      fontWeight={400}
                      lineHeight={1.2}
                      paragraph
                    >
                      We Secure Your Account With
                      <br />
                      Two-Factor Authentication
                      <br />
                      <Box
                        component="span"
                        sx={{ color: 'primary.main', fontWeight: 700 }}
                      >
                        Verify Your Account
                      </Box>
                    </Typography>
                  )}
                  {needVerification ? (
                    <React.Fragment>
                      <OTPInput
                        disabled={loading}
                        length={6}
                        isNumberInput
                        autoFocus
                        onComplete={this.onOTPCompleted}
                        sx={{ mx: 'auto', mt: 4 }}
                      />
                      <Box
                        mt={1}
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        {enableSend && (
                          <Button variant="text" onClick={this.resendCode}>
                            {t('auth:resend-code', 'Resend Code')}
                          </Button>
                        )}
                      </Box>
                      <Box mt={1} textAlign="center">
                        {!enableSend && (
                          <Typography color="inherit" variant="caption">
                            <Trans ns="auth" i18nKey="request-code-time-left">
                              You can request for code again in {{ timeLeft }}
                              s.
                            </Trans>
                          </Typography>
                        )}
                      </Box>
                    </React.Fragment>
                  ) : !invalid ? (
                    <React.Fragment>
                      <FormItem>
                        {getFieldDecorator('password', {
                          rules: [
                            {
                              required: true,
                              message: t(
                                'auth:enter-password',
                                'Please enter password',
                              ),
                            },
                          ],
                        })(
                          <TextField
                            fullWidth
                            autoFocus
                            name="password"
                            placeholder={t('auth:new-password', 'New Password')}
                            type="password"
                            margin="dense"
                          />,
                        )}
                      </FormItem>
                      <FormItem>
                        {getFieldDecorator('confirmPassword', {
                          rules: [
                            {
                              required: true,
                              message: t(
                                'auth:confirm-password',
                                'Please confirm password',
                              ),
                            },
                          ],
                        })(
                          <TextField
                            name="confirmPassword"
                            placeholder={t(
                              'auth:confirm-password',
                              'Please confirm password',
                            )}
                            type="password"
                            fullWidth
                            margin="dense"
                          />,
                        )}
                      </FormItem>
                      <Box mt={1}>
                        <Button
                          fullWidth
                          onClick={this.handleSubmit}
                          variant="contained"
                          color="primary"
                          size="large"
                        >
                          {t(
                            'auth:create-password-submit',
                            'Create New Password',
                          )}
                        </Button>
                      </Box>
                    </React.Fragment>
                  ) : (
                    <div>
                      <Alert severity="error">
                        <AlertTitle>
                          {t('invalid-number-header', 'Oops!')}
                        </AlertTitle>
                        {t(
                          'invalid-number-message',
                          "Your mobile number couldn't be verified. Please contact support to correct this.",
                        )}
                      </Alert>
                    </div>
                  )}
                  <Divider sx={{ my: 2 }} />
                  {needVerification && (
                    <Box>
                      <Trans ns="auth" i18nKey="create-password-enter-code">
                        To verify your account, enter the verification code that
                        has been sent to a phone number ending with
                        <strong>{{ last4 }}</strong>. If you need help contact
                        support@textinchurch.com.
                      </Trans>
                    </Box>
                  )}
                </Paper>
              )}
            </>
          </Form>
        </Box>
      </Styled.Container>
    );
  }
}

export default Form.create()(withTranslation(['auth'])(ResetPassword));
